import React, { useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons';
import { GTFonts, useTheme, withOpacity, type Theme } from '../../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../../utils/libs';

/**
 * Stateless, accessible activation task row.
 * Owns no completion rules: everything is injected by the tracker.
 */

export type ActivationTaskRowProps = {
  /** Exact task label, e.g. "Add your first product". */
  label: string;
  /** One-line value explanation under the label. */
  description: string;
  completed: boolean;
  /** Visually emphasized next recommended action. */
  highlighted?: boolean;
  /** CTA label while incomplete, e.g. "Add product". */
  ctaLabel?: string;
  /** Optional dependency/status note, e.g. "Add a product first". */
  dependencyNote?: string;
  /** Disable the CTA (dependency missing, location switching, loading). */
  disabled?: boolean;
  /** Busy state, e.g. "Still preparing" while test location provisions. */
  busy?: boolean;
  onPress?: () => void;
  testID?: string;
};

const ActivationTaskRow: React.FC<ActivationTaskRowProps> = ({
  label,
  description,
  completed,
  highlighted = false,
  ctaLabel,
  dependencyNote,
  disabled = false,
  busy = false,
  onPress,
  testID,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const interactive = !completed && !!onPress;
  const accessibilityState = {
    disabled: disabled || !interactive,
    checked: completed,
    busy,
  };
  const accessibilityLabel = completed
    ? `${label}, completed`
    : dependencyNote
      ? `${label}, not completed. ${dependencyNote}`
      : `${label}, not completed`;

  const content = (
    <>
      <View
        style={[
          styles.statusIcon,
          completed
            ? styles.statusIconComplete
            : highlighted
              ? styles.statusIconHighlighted
              : styles.statusIconIncomplete,
        ]}>
        <Ionicons
          name={completed ? 'checkmark' : highlighted ? 'arrow-forward' : 'ellipse-outline'}
          size={iconSize(completed || highlighted ? 16 : 12)}
          color={
            completed
              ? theme.colors.surface
              : highlighted
                ? theme.colors.surface
                : theme.colors.textSecondary
          }
        />
      </View>

      <View style={styles.textColumn}>
        <Text
          style={[styles.label, completed && styles.labelComplete]}
          numberOfLines={2}>
          {label}
        </Text>
        <Text style={styles.description} numberOfLines={2}>
          {completed ? 'Done' : description}
        </Text>
        {!completed && !!dependencyNote && (
          <Text style={styles.dependencyNote} numberOfLines={2}>
            {dependencyNote}
          </Text>
        )}
      </View>

      {!completed && !!ctaLabel && (
        <View
          style={[
            styles.ctaBadge,
            highlighted && styles.ctaBadgeHighlighted,
            (disabled || busy) && styles.ctaBadgeDisabled,
          ]}>
          <Text
            style={[
              styles.ctaText,
              highlighted && styles.ctaTextHighlighted,
              (disabled || busy) && styles.ctaTextDisabled,
            ]}
            numberOfLines={1}>
            {busy ? 'Still preparing' : ctaLabel}
          </Text>
        </View>
      )}
      {completed && (
        <Text style={styles.completedText} accessibilityElementsHidden>
          Completed
        </Text>
      )}
    </>
  );

  if (!interactive) {
    return (
      <View
        testID={testID}
        accessible
        accessibilityRole={completed ? 'text' : 'button'}
        accessibilityLabel={accessibilityLabel}
        accessibilityState={accessibilityState}
        style={[styles.row, highlighted && styles.rowHighlighted]}>
        {content}
      </View>
    );
  }

  return (
    <TouchableOpacity
      testID={testID}
      accessible
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={ctaLabel}
      accessibilityState={accessibilityState}
      disabled={disabled}
      onPress={onPress}
      style={[
        styles.row,
        highlighted && styles.rowHighlighted,
        disabled && styles.rowDisabled,
      ]}>
      {content}
    </TouchableOpacity>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: v(10),
      paddingHorizontal: s(10),
      borderRadius: theme.borderRadius.md,
      gap: s(10),
      minHeight: v(48),
    },
    rowHighlighted: {
      backgroundColor: withOpacity(theme.colors.success, 0.08),
      borderWidth: borderWidth(1),
      borderColor: withOpacity(theme.colors.success, 0.35),
    },
    rowDisabled: {
      opacity: 0.6,
    },
    statusIcon: {
      width: iconSize(26),
      height: iconSize(26),
      borderRadius: iconSize(13),
      alignItems: 'center',
      justifyContent: 'center',
    },
    statusIconComplete: {
      backgroundColor: theme.colors.success,
    },
    statusIconHighlighted: {
      backgroundColor: theme.colors.success,
    },
    statusIconIncomplete: {
      backgroundColor: 'transparent',
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.borderGray,
    },
    textColumn: {
      flex: 1,
      gap: v(2),
    },
    label: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    labelComplete: {
      color: theme.colors.textSecondary,
    },
    description: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    dependencyNote: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.warningDark,
    },
    ctaBadge: {
      paddingHorizontal: s(10),
      paddingVertical: v(6),
      borderRadius: theme.borderRadius.sm,
      backgroundColor: withOpacity(theme.colors.success, 0.12),
    },
    ctaBadgeHighlighted: {
      backgroundColor: theme.colors.success,
    },
    ctaBadgeDisabled: {
      backgroundColor: theme.colors.border,
    },
    ctaText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.success,
    },
    ctaTextHighlighted: {
      color: theme.colors.surface,
    },
    ctaTextDisabled: {
      color: theme.colors.textSecondary,
    },
    completedText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.success,
    },
  });

export default ActivationTaskRow;
